import PropTypes from 'prop-types';
import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import Info from '../Info';
import './style.scss';

const CustomSlider = styled(Slider)(({ theme }) => ({
  //color: theme.palette.primary.main,
  color: '#006bef',
  height: 5,
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)',
    },
  },
  '& .MuiSlider-track': {
    height: 3,
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#006bef',
  },
  '& .MuiSlider-valueLabel': {
    fontSize: 12,
    fontWeight: 'normal',
    top: -6,
    backgroundColor: 'unset',
    color: theme.palette.text.primary,
    '&::before': {
      display: 'none',
    },
    '& *': {
      background: 'transparent',
      color: '#000',
      ...theme.applyStyles('dark', {
        color: '#fff',
      }),
    },
  },
}));

function SliderComponent({
  name,
  value,
  step,
  min,
  max,
  label,
  suffix,
  onChange,
  inSync,
  offset,
  factor,
  round,
  hint,
  disabled,
}) {
  const [currentValue, setCurrentValue] = useState(value);
  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const getDisplayValue = () => {
    let value = factor * currentValue + offset;
    if (round !== null) {
      value = Math.round(value);
    }
    return value;
  };

  const updateValue = useCallback((event, value) => {
    value = Math.floor((value - offset) / factor);
    setCurrentValue(value);
  }, [offset, factor]);

  const handleUpdate = useCallback((event, value) => {
    value = Math.floor((value - offset) / factor);
    setTimeout(() => {
      onChange(name, value);
    }, 100);
  }, [onChange, offset, factor, name]);

  const formatValue = useCallback((value) => {
    let displayValue = factor * value + offset;
    if (round !== null) {
      displayValue = Math.round(displayValue);
    }
    return `${displayValue}${suffix}`;
  }, [factor, offset, round, suffix]);

  return (
    <div className="slider-container">
      <div className="slider-wrapper">
        <div className="slider-control">
          <CustomSlider
            disabled={disabled}
            value={inSync ? getDisplayValue() : min}
            min={min}
            max={max}
            step={step}
            onChange={updateValue}
            onChangeCommitted={handleUpdate}
            valueLabelDisplay="on"
            /*valueLabelFormat={formatValue}*/
            aria-labelledby={`${name}-label`}
            //size="small"
          />
        </div>
        <label 
          className={`slider-label ${disabled ? 'disabled' : ''}`}
          id={`${name}-label`}
        >
          {label}
        </label>
        {hint && (
          <div className="slider-hint">
            <Info
              hint={hint}
              inSync={inSync}
              name={name}
            />
          </div>
        )}
      </div>
    </div>
  );
}

SliderComponent.defaultProps = {
  disabled: false,
  factor: 1,
  hint: null,
  inSync: true,
  max: 100,
  min: 0,
  offset: 0,
  round: null,
  step: 1,
  suffix: '',
  value: 0,
};

SliderComponent.propTypes = {
  disabled: PropTypes.bool,
  factor: PropTypes.number,
  hint: PropTypes.string,
  inSync: PropTypes.bool,
  label: PropTypes.string.isRequired,
  max: PropTypes.number,
  min: PropTypes.number,
  name: PropTypes.string.isRequired,
  offset: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  round: PropTypes.bool,
  step: PropTypes.number,
  suffix: PropTypes.string,
  value: PropTypes.number,
};

export default SliderComponent;
